"use client";

import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Camera, Upload, ChevronRight, Loader2 } from "lucide-react";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { submitBusinessDetailsRequest } from "@/store/slices/kyc.slice";
import { formDataUtils } from "@/services/kycService";
import { store } from "@/store/store";
import { InputPrimary } from "@/components/common/InputPrimary";
import { Button } from "@/components/ui/button";
import FormFieldWrapper from "@/components/coupon/formFieldWrapper";
import KYCErrorDisplay from "./KYCErrorDisplay";
import { KYCFieldError, useFieldHasErrors } from "./KYCFieldError";
import StateCitySelector from "@/components/common/StateCitySelector";

// Validation Schema - Business Details Only
const businessDetailsSchema = z.object({
  business_name: z.string().min(1, "Business name is required"),
  business_email: z.string().email("Please enter a valid email address"),
  business_phone_number: z
    .string()
    .min(1, "Phone number is required")
    .refine((val) => {
      const digits = val.replace(/\D/g, "");
      return digits.length === 10;
    }, "Please enter a valid 10-digit phone number"),
  address_line_one: z.string().min(1, "Address line 1 is required"),
  address_line_two: z.string().optional(),
  postal_code: z.string().min(1, "Postal code is required"),
});

type BusinessDetailsFormData = z.infer<typeof businessDetailsSchema>;

// Extended type to include state and city which are handled separately
// (handled inline where needed)

interface BusinessDetailsStepProps {
  storeId: string;
  kycData?: any | null;
}

export const BusinessDetailsStepNew: React.FC<BusinessDetailsStepProps> = ({
  storeId,
  kycData = null,
}) => {
  const dispatch = useAppDispatch();
  const { isLoading, error, kycProblems, rejectionReason, kycStatus } =
    useAppSelector((state) => state.kyc);
  const [businessLogo, setBusinessLogo] = useState<File | string | null>(
    kycData?.business_logo || null,
  );
  const [businessLogoPreview, setBusinessLogoPreview] = useState<string | null>(
    typeof kycData?.business_logo === "string" ? kycData.business_logo : null,
  );
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [selectedState, setSelectedState] = useState(
    kycData?.address?.state || "",
  );
  const [selectedCity, setSelectedCity] = useState(
    kycData?.address?.city || "",
  );
  const [stateError, setStateError] = useState<string | null>(null);
  const [cityError, setCityError] = useState<string | null>(null);

  // Check if specific fields have errors
  const businessNameHasError = useFieldHasErrors("business_name", kycProblems);
  const businessLogoHasError = useFieldHasErrors("business_logo", kycProblems);
  const businessEmailHasError = useFieldHasErrors(
    "business_email",
    kycProblems,
  );
  const businessPhoneHasError = useFieldHasErrors(
    "business_phone_number",
    kycProblems,
  );
  const addressHasError = useFieldHasErrors("address", kycProblems);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<BusinessDetailsFormData>({
    resolver: zodResolver(businessDetailsSchema),
    defaultValues: {
      business_name: kycData?.business_name || "",
      business_email: kycData?.business_email || "",
      business_phone_number: kycData?.business_phone_number || "",
      address_line_one: kycData?.address?.address_line_one || "",
      address_line_two: kycData?.address?.address_line_two || "",
      postal_code: kycData?.address?.postal_code || "",
    },
  });

  // debug effect removed: incoming kycData logging

  // Track if component has mounted to prevent unnecessary resets
  const [hasInitialized, setHasInitialized] = React.useState(false);
  
  // Track if form has been submitted to prevent resetting user input on errors
  const [hasSubmitted, setHasSubmitted] = React.useState(false);

  // Clear the error when the component successfully processes a submission
  React.useEffect(() => {
    if (!error && !isLoading) {
      setSubmitError(null);
    }
  }, [error, isLoading]);

  // Only reset form with kycData on initial load or when user first sees the component
  // Do NOT reset if user has already submitted (to preserve their input during errors)
  React.useEffect(() => {
    if (kycData && !hasInitialized && !hasSubmitted) {
      console.debug(
        "[BusinessDetailsStepNew] initial form setup with kycData:",
        kycData,
      );
      reset({
        business_name: kycData.business_name || "",
        business_email: kycData.business_email || "",
        business_phone_number: kycData.business_phone_number || "",
        address_line_one: kycData.address?.address_line_one || "",
        address_line_two: kycData.address?.address_line_two || "",
        postal_code: kycData.address?.postal_code || "",
      });
      if (kycData.business_logo) {
        console.debug(
          "[BusinessDetailsStepNew] setting businessLogo from kycData:",
          kycData.business_logo,
        );
        setBusinessLogo(kycData.business_logo);
        setBusinessLogoPreview(
          typeof kycData.business_logo === "string"
            ? kycData.business_logo
            : null,
        );
      }
      setHasInitialized(true);
    }
  }, [kycData, reset, hasInitialized, hasSubmitted]);

  // debug effect removed: form reset preview logging

  // Create object URL for local File previews and manage lifecycle
  React.useEffect(() => {
    let url: string | null = null;
    if (businessLogo && typeof businessLogo !== "string") {
      url = URL.createObjectURL(businessLogo as File);
      setBusinessLogoPreview(url);
    } else if (businessLogo && typeof businessLogo === "string") {
      setBusinessLogoPreview(businessLogo);
    } else {
      setBusinessLogoPreview(null);
    }

    return () => {
      if (url) {
        URL.revokeObjectURL(url);
      }
    };
  }, [businessLogo]);

  // Only update state and city on initial load to avoid resetting user input
  React.useEffect(() => {
    if (kycData && !hasInitialized && !hasSubmitted) {
      if (kycData.address?.state) {
        setSelectedState(kycData.address.state);
      }
      if (kycData.address?.city) {
        setSelectedCity(kycData.address.city);
      }
    }
  }, [kycData, hasInitialized, hasSubmitted]);

  const onSubmit = async (
    data: BusinessDetailsFormData,
    e?: React.BaseSyntheticEvent,
  ) => {
    // Prevent any default form submission behavior
    if (e) {
      e.preventDefault();
    }
    try {
      setSubmitError(null);
      setStateError(null);
      setCityError(null);
      
      // Mark that user has attempted submission to prevent form resets
      setHasSubmitted(true);

      // Validate state and city
      if (!selectedState.trim()) {
        setStateError("State is required");
        return;
      }
      if (!selectedCity.trim()) {
        setCityError("City is required");
        return;
      }

      // Create FormData for submission including state and city
      const businessPayload: Record<string, any> = {
        ...data,
        state: selectedState,
        city: selectedCity,
      };
      // Only attach business_logo if it's a local File (not a remote URL string)
      if (businessLogo && typeof businessLogo !== "string") {
        businessPayload.business_logo = businessLogo;
      }

      const businessFormData = formDataUtils.createBusinessDetailsFormData(
        businessPayload as any,
      );

      await new Promise((resolve, reject) => {
        const unsubscribe = store.subscribe(() => {
          const state = store.getState();
          const last = state.kyc.lastAction;
          // We only react when lastAction indicates submit finished for business details
          if (
            last === "submitBusinessDetailsSuccess" ||
            last === "submitBusinessDetailsFailure"
          ) {
            unsubscribe();
            if (last === "submitBusinessDetailsFailure") {
              reject(
                new Error(
                  state.kyc.error || "Failed to submit business details",
                ),
              );
            } else {
              // Success case - clear any persisted error marker
              resolve(true);
            }
          }
        });

        dispatch(
          submitBusinessDetailsRequest({
            storeId,
            formData: businessFormData,
          }),
        );
      });

      // Clear lastAction so subsequent submissions will wait correctly
      dispatch({ type: "kyc/clearError" });
    } catch (error: unknown) {
      setSubmitError(
        error instanceof Error
          ? error.message
          : "Failed to submit business details",
      );
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <h3 className="text-lg font-semibold text-foreground mb-1">
          Business Information
        </h3>
        <p className="text-sm text-muted-foreground">
          Please provide your business details to continue with the onboarding
          process.
        </p>
      </div>

      {/* KYC Error Display */}
      {kycStatus === "REJECTED" && (kycProblems?.length || rejectionReason) && (
        <KYCErrorDisplay
          problems={kycProblems}
          rejectionReason={rejectionReason}
        />
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        {/* Logo Upload */}
        <FormFieldWrapper title="Business Logo">
          <div className="flex items-center gap-4">
            {/* Clickable Image Area */}
            <div
              className={`w-16 h-16 bg-secondary border-2 border-dashed rounded-lg flex items-center justify-center overflow-hidden cursor-pointer transition-all hover:border-primary hover:bg-secondary/80 ${
                businessLogoHasError
                  ? "border-orange-500 bg-orange-50 dark:bg-orange-950/20"
                  : "border-border"
              }`}
              onClick={() => document.getElementById("logo-upload")?.click()}
            >
              {businessLogoPreview ? (
                <img
                  src={businessLogoPreview}
                  alt="Business Logo"
                  className="w-12 h-12 object-cover rounded"
                />
              ) : (
                <Upload className="w-6 h-6 text-muted-foreground" />
              )}
            </div>

            {/* Upload Text Area */}
            <div
              className="flex-1 cursor-pointer"
              onClick={() => document.getElementById("logo-upload")?.click()}
            >
              <div className="p-3 border border-dashed border-border rounded-lg hover:border-primary hover:bg-secondary/50 transition-all">
                <div className="flex items-center gap-2 mb-1">
                  <Camera className="w-4 h-4 text-primary" />
                  <span className="text-sm font-medium text-foreground">
                    {businessLogo || kycData?.business_logo
                      ? "Change Logo"
                      : "Upload Logo"}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">
                  Click here or on the image • JPEG/PNG, max 2MB
                </p>
              </div>
            </div>

            <input
              id="logo-upload"
              type="file"
              accept="image/jpeg,image/png"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  setBusinessLogo(file);
                }
              }}
            />
          </div>
          <KYCFieldError
            fieldName="business_logo"
            problems={kycProblems || []}
          />
        </FormFieldWrapper>

        {/* Business Details */}
        <FormFieldWrapper title="Business Details">
          <div className="space-y-[10px]">
            <div>
              <label className="block text-[13px] font-normal mb-1">
                Business Name <span className="text-destructive">*</span>
              </label>
              <InputPrimary
                id="business_name"
                {...register("business_name")}
                placeholder="Enter business name"
                className={`bg-transparent border rounded-xl h-[50px] px-4 py-2 placeholder-gray-500 ${
                  businessNameHasError ? "border-orange-500 bg-orange-50" : ""
                }`}
              />
              {errors.business_name && (
                <p className="text-destructive text-sm">
                  {errors.business_name.message}
                </p>
              )}
              <KYCFieldError
                fieldName="business_name"
                problems={kycProblems || []}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-[13px] font-normal mb-1">
                  Email <span className="text-destructive">*</span>
                </label>
                <InputPrimary
                  id="business_email"
                  type="email"
                  {...register("business_email")}
                  placeholder="business@example.com"
                  className={`bg-transparent border rounded-xl h-[50px] px-4 py-2 placeholder-gray-500 ${
                    businessEmailHasError
                      ? "border-orange-500 bg-orange-50"
                      : ""
                  }`}
                />
                {errors.business_email && (
                  <p className="text-destructive text-sm">
                    {errors.business_email.message}
                  </p>
                )}
                <KYCFieldError
                  fieldName="business_email"
                  problems={kycProblems || []}
                />
              </div>

              <div>
                <label className="block text-[13px] font-normal mb-1">
                  Phone Number <span className="text-destructive">*</span>
                </label>
                <InputPrimary
                  id="business_phone_number"
                  {...register("business_phone_number", {
                    onChange: (e) => {
                      const inputValue = e.target.value;
                      if (!/^[\d]*$/.test(inputValue)) {
                        e.target.value = inputValue.replace(/\D/g, "");
                      }
                      if (e.target.value.length > 10) {
                        e.target.value = e.target.value.slice(0, 10);
                      }
                    },
                  })}
                  placeholder="9876543210"
                  maxLength={10}
                  className={`bg-transparent border rounded-xl h-[50px] px-4 py-2 placeholder-gray-500 ${
                    businessPhoneHasError
                      ? "border-orange-500 bg-orange-50"
                      : ""
                  }`}
                />
                {errors.business_phone_number && (
                  <p className="text-destructive text-sm">
                    {errors.business_phone_number.message}
                  </p>
                )}
                <KYCFieldError
                  fieldName="business_phone_number"
                  problems={kycProblems || []}
                />
              </div>
            </div>
          </div>
        </FormFieldWrapper>

        {/* Address Details */}
        <FormFieldWrapper title="Address Details">
          {addressHasError && (
            <KYCFieldError
              fieldName="address"
              problems={kycProblems || []}
              className="mb-4"
            />
          )}
          <div className="space-y-[10px]">
            <div>
              <label className="block text-[13px] font-normal mb-1">
                Address Line 1 <span className="text-destructive">*</span>
              </label>
              <InputPrimary
                id="address_line_one"
                {...register("address_line_one")}
                placeholder="Street address"
                className={`bg-transparent border rounded-xl h-[50px] px-4 py-2 placeholder-gray-500 ${
                  addressHasError ? "border-orange-500 bg-orange-50" : ""
                }`}
              />
              {errors.address_line_one && (
                <p className="text-destructive text-sm">
                  {errors.address_line_one.message}
                </p>
              )}
            </div>

            <div>
              <label className="block text-[13px] font-normal mb-1">
                Address Line 2
              </label>
              <InputPrimary
                id="address_line_two"
                {...register("address_line_two")}
                placeholder="Apartment, suite, etc. (optional)"
                className={`bg-transparent border rounded-xl h-[50px] px-4 py-2 placeholder-gray-500 ${
                  addressHasError ? "border-orange-500 bg-orange-50" : ""
                }`}
              />
            </div>

            {/* State and City Selector */}
            <StateCitySelector
              selectedState={selectedState}
              selectedCity={selectedCity}
              onStateChange={setSelectedState}
              onCityChange={setSelectedCity}
              stateError={stateError ?? undefined}
              cityError={cityError ?? undefined}
              stateHasError={addressHasError}
              cityHasError={addressHasError}
              required
            />

            <div>
              <label className="block text-[13px] font-normal mb-1">
                Postal Code <span className="text-destructive">*</span>
              </label>
              <InputPrimary
                id="postal_code"
                {...register("postal_code")}
                placeholder="123456"
                className={`bg-transparent border rounded-xl h-[50px] px-4 py-2 placeholder-gray-500 ${
                  addressHasError ? "border-orange-500 bg-orange-50" : ""
                }`}
              />
              {errors.postal_code && (
                <p className="text-destructive text-sm">
                  {errors.postal_code.message}
                </p>
              )}
            </div>
          </div>
        </FormFieldWrapper>

        {/* Error Message */}
        {(error || submitError) && (
          <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
            <p className="text-destructive text-sm">{error || submitError}</p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-between items-center pt-6">
          <Button
            type="button"
            variant="outline"
            disabled
            className="px-6 py-3 rounded-[30px] shadow-sm"
          >
            Previous
          </Button>

          <Button
            type="submit"
            disabled={isLoading}
            className="flex items-center space-x-2 px-6 py-3 bg-primary text-primary-foreground font-semibold hover:bg-primary/90 active:bg-primary/80 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl rounded-[30px] transform hover:scale-105 active:scale-95"
          >
            {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
            <span>{isLoading ? "Submitting..." : "Next"}</span>
            {!isLoading && <ChevronRight className="w-5 h-5" />}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default BusinessDetailsStepNew;
